import React from 'react';
import { useEffect } from 'react';
import { useMemo } from 'react';
import { useObservation } from 'apps/sigma2-ui/src/state/observation';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { useLocation } from 'react-router';
import { useLoadingObservationIds } from 'apps/sigma2-ui/src/lib/hooks/useLoadingObservationIds';
import { useUserObservationOrgAndDbIds } from 'apps/sigma2-ui/src/lib/hooks/useUserObservationOrgAndDbIds';

/**
 * Returns true if the observation is still loading.
 * @returns {boolean} True if the observation is still loading.
 */

export interface IsLoadingObservationHookResult {
  isLoadingObservation: boolean;
  failedToLoadObservation: boolean;
}

export const useIsLoadingObservation = (params?: {
  isDashboardDetailPage?: boolean;
}): IsLoadingObservationHookResult => {
  const { myObservation, failedToLoadObservation } = useObservation();
  const { myUser } = useUser();

  const { organizationId, databaseId, databaseRevisionId } =
    useLoadingObservationIds({
      isDashboardDetailPage: params?.isDashboardDetailPage,
    });

  const {
    selectedDatabaseRevisionId: userObsDatabaseRevisionId,
    selectedDatabaseId: userObsDatabaseId,
    selectedOrganizationId: userObsOrganizationId,
  } = useUserObservationOrgAndDbIds();

  const { pathname, hash } = useLocation();

  const isLoadingObservation = useMemo(() => {
    return (
      !failedToLoadObservation &&
      (!myObservation ||
        !myUser ||
        databaseId !== userObsDatabaseId ||
        databaseRevisionId !== userObsDatabaseRevisionId ||
        organizationId !== userObsOrganizationId)
    );
  }, [
    databaseId,
    databaseRevisionId,
    failedToLoadObservation,
    myObservation,
    myUser,
    organizationId,
    userObsDatabaseId,
    userObsDatabaseRevisionId,
    userObsOrganizationId,
  ]);

  // Scroll to the hash on page load as well as when the React Router changes the hash.
  useEffect(() => {
    if (isLoadingObservation) {
      return;
    }

    if (hash) {
      const anchorEl = document.querySelector(hash);
      if (anchorEl) {
        anchorEl.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash, isLoadingObservation]);

  return useMemo(() => {
    return {
      failedToLoadObservation,
      isLoadingObservation,
    };
  }, [failedToLoadObservation, isLoadingObservation]);
};
